import { useNavigate } from 'react-router-dom';
import { Check, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ProfileCompletionItem {
  label: string;
  description: string | undefined;
  completed: boolean;
  route?: string;
}

interface ProfileCompletionCardProps {
  percentage: number;
  items: ProfileCompletionItem[];
}

export function ProfileCompletionCard({ percentage, items }: ProfileCompletionCardProps) {
  const navigate = useNavigate();

  if (percentage >= 100) {
    return null;
  }

  return (
    <div className="w-full bg-slate-100 rounded-2xl sm:rounded-[27px] p-4 sm:p-5 space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-slate-500 text-sm">Profile Completion</span>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex-1 bg-slate-200 rounded-md h-3 overflow-hidden">
          <div
            className="h-full bg-emerald-500 transition-all duration-500"
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-2xl font-normal text-slate-900">{percentage}%</span>
      </div>

      {/* Completion Items */}
      <div className="space-y-2">
        {items.map((item, index) => (
          <div
            key={index}
            className={cn(
              "flex items-start gap-3 p-3 rounded-xl transition-colors",
              item.completed
                ? "bg-white/50"
                : "cursor-pointer hover:bg-slate-200/50"
            )}
            onClick={() => {
              if (!item.completed && item.route) {
                navigate(item.route);
              }
            }}
          >
            {item.completed && (
              <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 bg-emerald-500">
                <Check className="h-4 w-4 text-white" />
              </div>
            )}
            <div className="flex-1">
              <p className={cn(
                "text-sm font-semibold",
                item.completed ? "text-emerald-700" : "text-slate-900"
              )}>
                {item.label}
              </p>
              <p className="text-xs text-slate-500">
                {item.description}
              </p>
            </div>
            {!item.completed && (
              <ChevronRight className="h-5 w-5 text-slate-400" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
